import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Tab, TabList, Tabs } from 'react-tabs';
import { AppDivisionContext } from '../../contexts/AppDivisionContext';
import { RolesContext } from '../../contexts/RolesContext';
import TenantFeatureContext from '../../contexts/TenantContext';
import { useDivisionUsers } from '../../hooks/users';
import { useDisplayName, withDisplayNames } from '../../hooks/useTerminology';
import rolesApi from '../../utils/api/roles';
import {
  FormContext,
  FormContextProvider,
  FormSchemaFields,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import AddUsersInput from '../Users/AddUser/AddUsersInput';
import FormModal from '../Shared/form/modal/FormModal';
import Loading from '../Shared/ResourceIndex/Loading';
import ContactForm, { getInitialData } from './ContactForm';
import { Contact } from '../../utils/api/client';

export interface IAddContactModalProps {
  show: boolean;
  hideModal: () => void;
  currentContactIds: Array<number>;
  handleAdd: (ids: Array<number>) => void;
  handleInvite?: (emails: Array<string>, roleId: number) => Promise<any>;
  clientName?: string | null;
}

const AddContactModal: React.FC<IAddContactModalProps> = ({
  show,
  hideModal,
  currentContactIds,
  handleAdd,
  handleInvite,
  clientName,
}) => {
  const { appDivisionId, divisions } = useContext(AppDivisionContext);
  const { tenantId } = useContext(TenantFeatureContext);
  const displayName = useDisplayName();
  const [tabIndex, setTabIndex] = useState(0);
  const initialValue = useMemo(
    () => ({ ...getInitialData(), tags: [], roleId: null }),
    [],
  );
  const rolesQuery = useQuery(
    ['rolesByTenant', tenantId],
    () => rolesApi.getRolesByTenant(tenantId),
    { enabled: !!handleInvite && !!tenantId },
  );
  const roleStore = useContext(RolesContext);
  const [loaded, setLoaded] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<Array<Contact>>([]);
  const usersQuery = useDivisionUsers(appDivisionId!);

  useEffect(() => {
    if (!loaded) {
      roleStore.fetchRoles().then(() => setLoaded(true));
    }
  }, []);

  useEffect(() => {
    const { isLoading, data } = usersQuery;

    if (!isLoading && data && currentContactIds && currentContactIds.length) {
      setFilteredUsers(
        data.filter((user) => {
          return !currentContactIds.includes(user.id);
        }),
      );
    } else {
      setFilteredUsers(data || []);
    }
  }, [usersQuery.isLoading, currentContactIds]);

  if (!loaded || usersQuery.isLoading) {
    return <Loading what='contact form' />;
  }

  if (!show) {
    return null;
  }

  const handleSubmit = (formData: Record<string, any>) => {
    if (tabIndex === 1 && handleInvite) {
      return handleInvite(
        formData.tags.map((tag: { text: string }) => tag.text),
        formData.roleId,
      ).then(hideModal);
    }

    const submitResult: any = handleAdd(
      formData.users.map((_: Contact) => _.id),
    );

    if (submitResult instanceof Promise) {
      return submitResult.then(() => {
        hideModal();
      });
    } else {
      hideModal();

      return Promise.resolve();
    }
  };

  return (
    <FormContextProvider initialValue={initialValue}>
      <FormContext.Consumer>
        {(formContext) => {
          return (
            <FormModal
              modalProps={{
                title: 'Add a Contact',
                submitBtnText: 'Add',
              }}
              onCancel={hideModal}
              onSubmit={() => handleSubmit(formContext.value)}
            >
              {handleInvite && (
                <Tabs selectedIndex={tabIndex} onSelect={setTabIndex}>
                  <TabList>
                    <Tab>Existing user</Tab>
                    <Tab>Invite new user</Tab>
                  </TabList>
                </Tabs>
              )}
              {tabIndex === 1 ? (
                <div className='container'>
                  <p style={{ marginLeft: '-0.5rem' }}>
                    User will be added to{' '}
                    {divisions.find((d) => d.id === appDivisionId)?.name}{' '}
                    division and {clientName} client.
                  </p>
                  <FormSchemaFields
                    className='findings-form add-contact-form'
                    schema={{
                      tags: {
                        label: 'Email',
                        controlType: UIControlType.custom,
                        validation: { required: true },
                        renderControl: AddUsersInput,
                      },
                      roleId: {
                        label: 'Role',
                        controlType: UIControlType.select,
                        options: withDisplayNames(rolesQuery.data, displayName),
                        labelKey: 'displayName',
                        valueKey: 'id',
                        validation: { required: true },
                        parse: (val: string) => Number.parseInt(val, 10),
                      },
                    }}
                    formData={formContext.value}
                    onChange={formContext.set}
                  />
                </div>
              ) : (
                <ContactForm
                  onChange={formContext.set}
                  value={formContext.value}
                  users={filteredUsers}
                  noMatchMessage={
                    handleInvite
                      ? 'No user found, send an invite using the invite tab'
                      : undefined
                  }
                />
              )}
            </FormModal>
          );
        }}
      </FormContext.Consumer>
    </FormContextProvider>
  );
};

export default AddContactModal;
